/**
 * Cross-pane dirty-state tracker (#2237).
 *
 * Replaces the old `translationHandleRef` imperative handle. Each
 * open editor (source + translation) reports its own `isDirty`
 * through this context; the shell reads `anyDirty` for the
 * usePrompt / tab-close warning and for the "save before switching
 * language" rule.
 *
 * One dirty flag per open document, in one place — matches the
 * ticket's "Saving" bullet.
 */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

interface SplitDirtyContextValue {
  /** True iff at least one tracked document is dirty. */
  anyDirty: boolean;
  /** Child editors call this on every `isDirty` state change. */
  reportDirty: (documentId: string, isDirty: boolean) => void;
}

const SplitDirtyContext = createContext<SplitDirtyContextValue | null>(null);

export function useSplitDirty(): SplitDirtyContextValue {
  const ctx = useContext(SplitDirtyContext);
  // Safe no-op outside the provider so a single-document editor can
  // call `useReportDirty` without the parent having to mount the
  // provider too.
  return (
    ctx ?? {
      anyDirty: false,
      reportDirty: () => undefined
    }
  );
}

/**
 * Child editors call this hook with their current `isDirty`. The
 * value propagates up to the provider's `anyDirty`. Automatically
 * clears the entry on unmount.
 */
export function useReportDirty(
  documentId: string | null | undefined,
  isDirty: boolean
): void {
  const { reportDirty } = useSplitDirty();
  useEffect(() => {
    if (!documentId) return;
    reportDirty(documentId, isDirty);
  }, [documentId, isDirty, reportDirty]);
  useEffect(() => {
    if (!documentId) return;
    return () => reportDirty(documentId, false);
  }, [documentId, reportDirty]);
}

export function SplitDirtyProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags] = useState<Record<string, boolean>>({});

  const reportDirty = useCallback((documentId: string, isDirty: boolean) => {
    setFlags(prev => {
      const current = prev[documentId] ?? false;
      if (current === isDirty) return prev;
      // Drop the entry entirely when it goes clean — keeps the map
      // tight and makes `anyDirty` a cheap `Object.values(...).some`.
      if (!isDirty) {
        if (!(documentId in prev)) return prev;
        const { [documentId]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [documentId]: true };
    });
  }, []);

  const anyDirty = useMemo(() => Object.values(flags).some(Boolean), [flags]);

  const value = useMemo<SplitDirtyContextValue>(
    () => ({ anyDirty, reportDirty }),
    [anyDirty, reportDirty]
  );

  return (
    <SplitDirtyContext.Provider value={value}>
      {children}
    </SplitDirtyContext.Provider>
  );
}
